<template>
    <app-drawer
        class="incident-report-drawer"
        :drawer-id="drawerId"
        drawer-width="76vw"
        :drawer-header-class="'incident-report-drawer__drawer-header'"
        :drawer-body-class="'incident-report-drawer__body'"
        :drawer-panel-class="'incident-report-drawer__panel'"
        @close-modal="closeDrawer"
    >
        <template slot="header">
            <div class="incident-report-drawer__header">
                <div class="incident-report-drawer__header-copy">
                    <span class="incident-report-drawer__eyebrow">{{ $t('incident_report') }}</span>
                    <h5 class="modal-title">{{ reportTitle }}</h5>

                    <div v-if="report" class="incident-report-drawer__header-meta">
                        <span class="incident-report-drawer__code">
                            {{ report.incident_code || '---' }}
                        </span>

                        <span :class="`badge badge-pill badge-${severityBadge}`">
                            {{ report.severity ? $t(report.severity) : '---' }}
                        </span>

                        <span :class="`badge badge-pill badge-${statusBadge}`">
                            {{ report.status ? $t(report.status) : '---' }}
                        </span>
                    </div>
                </div>

                <div class="incident-report-drawer__header-actions">
                    <button
                        type="button"
                        class="btn btn-primary incident-report-drawer__pdf-button"
                        :disabled="!report"
                        @click.prevent="downloadPdf"
                    >
                        <app-icon :name="'download'" class="size-14 mr-1" />
                        {{ $t('download_pdf') }}
                    </button>

                    <button type="button" class="close outline-none" @click.prevent="closeDrawer">
                        <span>
                            <app-icon :name="'x'"></app-icon>
                        </span>
                    </button>
                </div>
            </div>
        </template>

        <template slot="body">
            <div v-if="isLoading" class="incident-report-drawer__state">
                <app-overlay-loader />
            </div>

            <div v-else-if="report" class="incident-report-drawer__content">
                <!-- <div class="incident-report-drawer__nav">
                    <button
                        v-for="section in sectionLinks"
                        :key="section.ref"
                        type="button"
                        class="incident-report-drawer__nav-pill"
                        @click.prevent="scrollToSection(section.ref)"
                    >
                        {{ section.label }}
                    </button>
                </div> -->

                <section ref="overview" class="incident-report-drawer__section">
                    <div class="incident-report-drawer__section-head">
                        <span class="incident-report-drawer__section-kicker">{{ $t('general_informations') }}</span>
                        <h6 class="mb-0">{{ $t('report_overview') }}</h6>
                    </div>

                    <div class="incident-report-drawer__overview-grid">
                        <article
                            v-for="card in overviewCards"
                            :key="card.key"
                            class="incident-report-drawer__overview-card"
                        >
                            <span class="incident-report-drawer__overview-icon">
                                <app-icon :name="card.icon" class="size-14" />
                            </span>
                            <div>
                                <small>{{ card.label }}</small>
                                <strong>{{ card.value || '---' }}</strong>
                            </div>
                        </article>
                    </div>
                </section>

                <section ref="narrative" class="incident-report-drawer__section">
                    <div class="incident-report-drawer__section-head">
                        <span class="incident-report-drawer__section-kicker">{{ $t('incident_reporting') }}</span>
                        <h6 class="mb-0">{{ $t('narrative') }}</h6>
                    </div>

                    <div v-if="report.reporting" class="incident-report-drawer__narrative" v-html="report.reporting"></div>
                    <div v-else class="incident-report-drawer__empty">
                        {{ $t('no_reporting_available') }}
                    </div>
                </section>

                <section ref="operations" class="incident-report-drawer__section">
                    <div class="incident-report-drawer__section-head">
                        <span class="incident-report-drawer__section-kicker">{{ $t('operational_brief') }}</span>
                        <h6 class="mb-0">{{ $t('trip_context') }}</h6>
                    </div>

                    <div class="incident-report-drawer__context-grid">
                        <article class="incident-report-drawer__context-card">
                            <span class="incident-report-drawer__context-label">{{ $t('route') }}</span>
                            <strong>{{ report.route.origin || '---' }}</strong>
                            <span>{{ report.route.destination || '---' }}</span>
                            <small v-if="report.route.current_location">{{ report.route.current_location }}</small>
                        </article>

                        <article class="incident-report-drawer__context-card">
                            <span class="incident-report-drawer__context-label">{{ $t('product') }}</span>
                            <strong>{{ report.trip.product_name || '---' }}</strong>
                            <div class="incident-report-drawer__mini-facts">
                                <span>{{ bundlesLabel }}</span>
                                <span>{{ netWeightLabel }}</span>
                            </div>
                        </article>

                        <article class="incident-report-drawer__context-card">
                            <span class="incident-report-drawer__context-label">{{ $t('transporter') }}</span>
                            <strong>{{ report.trip.transporter_name || '---' }}</strong>
                            <span>{{ report.device.imei || report.device.name || '---' }}</span>
                        </article>

                        <article class="incident-report-drawer__context-card">
                            <span class="incident-report-drawer__context-label">{{ $t('people') }}</span>
                            <strong>{{ report.trip.driver_name || '---' }}</strong>
                            <span>{{ report.escorter.name || '---' }}</span>
                            <div class="incident-report-drawer__contact-list">
                                <a
                                    v-if="report.trip.driver_number"
                                    :href="`tel:${report.trip.driver_number}`"
                                    class="incident-report-drawer__contact-chip"
                                >
                                    <app-icon :name="'phone'" class="size-12 mr-1" />
                                    {{ report.trip.driver_number }}
                                </a>
                                <a
                                    v-if="report.escorter.phone"
                                    :href="`tel:${report.escorter.phone}`"
                                    class="incident-report-drawer__contact-chip"
                                >
                                    <app-icon :name="'phone'" class="size-12 mr-1" />
                                    {{ report.escorter.phone }}
                                </a>
                            </div>
                        </article>

                        <article class="incident-report-drawer__context-card incident-report-drawer__context-card--location">
                            <span class="incident-report-drawer__context-label">{{ $t('location') }}</span>
                            <strong>{{ report.location.label || '---' }}</strong>
                            <span>{{ coordinatesLabel }}</span>
                            <a
                                v-if="report.location.google_maps_url"
                                :href="report.location.google_maps_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="incident-report-drawer__link"
                            >
                                {{ $t('open_in_maps') }}
                            </a>
                        </article>
                    </div>
                </section>

                <section ref="attachments" class="incident-report-drawer__section">
                    <div class="incident-report-drawer__section-head">
                        <span class="incident-report-drawer__section-kicker">{{ $t('documents') }}</span>
                        <h6 class="mb-0">{{ $t('attachments') }}</h6>
                    </div>

                    <div v-if="report.attachments && report.attachments.length" class="incident-report-drawer__attachments">
                        <a
                            v-for="attachment in report.attachments"
                            :key="attachment.key"
                            :href="attachment.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="incident-report-drawer__attachment"
                        >
                            <span class="incident-report-drawer__attachment-icon">
                                <app-icon :name="attachment.icon || 'file-text'" class="size-16" />
                            </span>
                            <div>
                                <strong>{{ attachment.label }}</strong>
                                <span>{{ attachment.type_label || $t('view_document') }}</span>
                            </div>
                        </a>
                    </div>
                    <div v-else class="incident-report-drawer__empty">
                        {{ $t('no_document_available') }}
                    </div>
                </section>
            </div>

            <div v-else class="incident-report-drawer__empty incident-report-drawer__empty--full">
                {{ $t('failed_response') }}
            </div>
        </template>
    </app-drawer>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { INCIDENT_REPORT } from '../../../Config/ApiUrl';
import { numberFormatter } from '../../../Helpers/Helpers';

export default {
    name: 'IncidentReportPreviewDrawer',
    extends: CoreLibrary,
    props: {
        incidentId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            report: null,
        };
    },
    computed: {
        drawerId() {
            return `incident-report-preview-drawer-${this.incidentId}`;
        },
        reportTitle() {
            if (!this.report) {
                return this.$t('incident_report');
            }

            return this.report.name || this.report.incident_code || this.$t('incident_report');
        },
        severityBadge() {
            const colorMap = {
                low: 'secondary',
                medium: 'warning',
                high: 'danger',
                critical: 'dark',
            };

            return colorMap[this.report?.severity] || 'secondary';
        },
        statusBadge() {
            const colorMap = {
                reported: 'warning',
                acknowledged: 'info',
                in_progress: 'primary',
                resolved: 'success',
                closed: 'secondary',
            };

            return colorMap[this.report?.status] || 'secondary';
        },
        sectionLinks() {
            return [
                { ref: 'overview', label: this.$t('report_overview') },
                { ref: 'narrative', label: this.$t('narrative') },
                { ref: 'operations', label: this.$t('trip_context') },
                { ref: 'attachments', label: this.$t('attachments') },
            ];
        },
        overviewCards() {
            if (!this.report) {
                return [];
            }

            return [
                { key: 'category', label: this.$t('category'), value: this.report.category ? this.$t(this.report.category_label) : '---', icon: 'layers' },
                { key: 'type', label: this.$t('incident_type'), value: this.report.type ? this.$t(this.report.type) : '---', icon: 'alert-octagon' },
                { key: 'material-loss', label: this.$t('confirmed_material_loss'), value: this.materialLossLabel, icon: 'package' },
                { key: 'source', label: this.$t('source'), value: this.report.source ? this.$t(this.report.source) : '---', icon: 'radio' },
                { key: 'occurred_at', label: this.$t('occurred_at'), value: this.report.occurred_at_label || '---', icon: 'clock' },
                { key: 'reported_at', label: this.$t('reported_at'), value: this.report.reported_at_label || '---', icon: 'calendar' },
                { key: 'delay', label: this.$t('delay_minutes'), value: this.delayLabel, icon: 'timer' },
            ].concat(this.creditNoteOverviewCards);
        },
        creditNoteOverviewCards() {
            if (!this.report?.credit_note?.applicable) {
                return [];
            }

            return [
                {
                    key: 'credit-note-generated',
                    label: this.$t('credit_note_generated'),
                    value: this.report.credit_note.generated ? this.$t('yes') : this.$t('no'),
                    icon: 'file-text',
                },
                {
                    key: 'credit-note-reference',
                    label: this.$t('credit_note_reference'),
                    value: this.report.credit_note.reference || '---',
                    icon: 'hash',
                },
                {
                    key: 'credit-note-generated-at',
                    label: this.$t('credit_note_generated_at'),
                    value: this.report.credit_note.generated_at_label || '---',
                    icon: 'calendar',
                },
            ];
        },
        materialLossLabel() {
            const status = this.report?.material_loss?.status;

            if (!status) {
                return '---';
            }

            return status === 'under_investigation'
                ? this.$t('under_investigation')
                : this.$t(status);
        },
        delayLabel() {
            if (!this.report || this.report.delay_minutes === null || this.report.delay_minutes === undefined || this.report.delay_minutes === '') {
                return '---';
            }

            return `${this.report.delay_minutes} min`;
        },
        bundlesLabel() {
            const bundles = this.report?.trip?.bundles;

            if (bundles === null || bundles === undefined || bundles === '') {
                return '---';
            }

            return this.formatMetric(bundles, 'bundle', 'bundles');
        },
        netWeightLabel() {
            const weight = this.report?.trip?.net_weight;

            if (weight === null || weight === undefined || weight === '') {
                return '---';
            }

            return this.formatMetric(weight, 'tonne', 'tonnes');
        },
        coordinatesLabel() {
            if (!this.report?.location) {
                return '---';
            }

            const latitude = this.report.location.latitude;
            const longitude = this.report.location.longitude;

            if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
                return '---';
            }

            return `${latitude}, ${longitude}`;
        },
    },
    methods: {
        fetchReport() {
            this.isLoading = true;

            this.axiosGet(`${INCIDENT_REPORT}/${this.incidentId}`)
                .then(({ data }) => {
                    this.report = data;
                })
                .catch(({ response }) => {
                    this.$toastr.e(response?.data?.message || this.$t('failed_response'));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        downloadPdf() {
            if (!this.report?.download_pdf_url) {
                return;
            }

            window.open(this.report.download_pdf_url, '_blank', 'noopener');
        },
        formatMetric(value, unitKey = '', pluralUnitKey = '') {
            const numericValue = Number(value);
            let formattedValue = `${value}`;

            if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
                formattedValue = Number.isInteger(numericValue) ? `${numericValue}` : numberFormatter(numericValue);
            }

            if (!unitKey) {
                return formattedValue;
            }

            const isPlural = !Number.isNaN(numericValue) && Number.isFinite(numericValue)
                ? Math.abs(numericValue) !== 1
                : true;
            const resolvedUnitKey = isPlural && pluralUnitKey ? pluralUnitKey : unitKey;

            return `${formattedValue} ${this.$t(resolvedUnitKey)}`;
        },
        scrollToSection(refName) {
            const section = this.$refs[refName];

            if (section && typeof section.scrollIntoView === 'function') {
                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        },
        closeDrawer() {
            this.$emit('close-modal');
        },
    },
    created() {
        this.fetchReport();
    },
};
</script>

<style>
.incident-report-drawer__panel {
    border-radius: 0 !important;
}

.incident-report-drawer__drawer-header {
    padding: 1.5rem 1.85rem 1.2rem;
    border-bottom: 1px solid var(--default-border-color);
}

.incident-report-drawer__body {
    padding: 1.55rem 1.85rem 2rem;
    background: var(--body-bg);
}

.incident-report-drawer__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.4rem;
    width: 100%;
}

.incident-report-drawer__header-copy {
    min-width: 0;
}

.incident-report-drawer__eyebrow {
    display: inline-flex;
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--brand-primary);
}

.incident-report-drawer__header-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    margin-top: 0.95rem;
}

.incident-report-drawer__code {
    display: inline-flex;
    align-items: center;
    padding: 0.32rem 0.72rem;
    border-radius: 0;
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.incident-report-drawer__header-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
}

.incident-report-drawer__pdf-button {
    border-radius: 0;
    white-space: nowrap;
    padding-inline: 0.95rem;
}

.incident-report-drawer__state,
.incident-report-drawer__empty--full {
    min-height: 22rem;
}

.incident-report-drawer__content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.incident-report-drawer__nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    position: sticky;
    top: 0;
    z-index: 4;
    padding-bottom: 0.5rem;
    background: var(--body-bg);
}

.incident-report-drawer__nav-pill {
    border: 1px solid rgba(var(--brand-primary-rgb), 0.14);
    border-radius: 0;
    padding: 0.5rem 0.95rem;
    background: var(--base-color);
    color: var(--default-font-color);
    font-size: 0.82rem;
    font-weight: 600;
    transition: 0.2s ease;
}

.incident-report-drawer__nav-pill:hover {
    color: var(--brand-primary);
    border-color: rgba(var(--brand-primary-rgb), 0.26);
    background: rgba(var(--brand-primary-rgb), 0.05);
}

.incident-report-drawer__section {
    padding: 1.45rem 1.5rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0;
    background: var(--base-color);
}

.incident-report-drawer__section-head {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-bottom: 1rem;
}

.incident-report-drawer__section-kicker {
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 700;
    color: var(--muted-font-color);
}

.incident-report-drawer__overview-grid,
.incident-report-drawer__context-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
}

.incident-report-drawer__overview-card,
.incident-report-drawer__context-card {
    display: flex;
    gap: 0.85rem;
    padding: 1.1rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0;
    background: rgba(var(--brand-primary-rgb), 0.03);
}

.incident-report-drawer__overview-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 0;
    background: rgba(var(--brand-primary-rgb), 0.11);
    color: var(--brand-primary);
    flex: 0 0 auto;
}

.incident-report-drawer__overview-card small,
.incident-report-drawer__context-label {
    display: block;
    margin-bottom: 0.25rem;
    color: var(--muted-font-color);
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.incident-report-drawer__overview-card strong,
.incident-report-drawer__context-card strong {
    display: block;
    font-size: 0.98rem;
    line-height: 1.45;
}

.incident-report-drawer__context-card span:not(.incident-report-drawer__context-label),
.incident-report-drawer__context-card small {
    display: block;
    margin-top: 0.18rem;
    color: var(--muted-font-color);
}

.incident-report-drawer__mini-facts,
.incident-report-drawer__contact-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.65rem;
}

.incident-report-drawer__mini-facts span,
.incident-report-drawer__contact-chip {
    display: inline-flex !important;
    align-items: center;
    padding: 0.3rem 0.65rem;
    border-radius: 0;
    background: var(--body-bg);
    color: var(--default-font-color) !important;
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: none;
}

.incident-report-drawer__context-card--location {
    grid-column: 1 / -1;
}

.incident-report-drawer__link {
    display: inline-flex;
    margin-top: 0.7rem;
    color: var(--brand-primary);
    font-weight: 600;
}

.incident-report-drawer__narrative {
    min-height: 12rem;
    padding: 1.15rem 1.2rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0;
    background: var(--body-bg);
    line-height: 1.75;
}

.incident-report-drawer__narrative img {
    max-width: 100%;
    height: auto;
    border-radius: 0.9rem;
    margin: 0.8rem 0;
}

.incident-report-drawer__attachments {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 1rem;
}

.incident-report-drawer__attachment {
    display: flex;
    gap: 0.9rem;
    align-items: flex-start;
    padding: 1.05rem 1.1rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0;
    background: rgba(var(--brand-primary-rgb), 0.03);
    text-decoration: none;
}

.incident-report-drawer__attachment-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 0;
    background: rgba(var(--brand-primary-rgb), 0.1);
    color: var(--brand-primary);
    flex: 0 0 auto;
}

.incident-report-drawer__attachment strong {
    display: block;
    color: var(--default-font-color);
}

.incident-report-drawer__attachment span {
    display: block;
    margin-top: 0.2rem;
    color: var(--muted-font-color);
    font-size: 0.84rem;
}

.incident-report-drawer__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 10rem;
    padding: 1rem;
    border: 1px dashed var(--default-border-color);
    border-radius: 0;
    background: var(--body-bg);
    color: var(--muted-font-color);
    text-align: center;
}

@media (max-width: 1199px) {
    .incident-report-drawer__overview-grid,
    .incident-report-drawer__context-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 991px) {
    .incident-report-drawer__drawer-header,
    .incident-report-drawer__body {
        padding-inline: 1rem;
    }

    .incident-report-drawer__header {
        flex-direction: column;
    }

    .incident-report-drawer__header-actions {
        width: 100%;
        justify-content: space-between;
    }
}
</style>
